import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import {
  VisualOnderzoek,
  VisualDesign,
  VisualOntwikkeling,
  VisualAnalyseren,
} from "@/components/BenefitVisuals";

interface Benefit {
  title: string;
  description: string;
  icon: React.ReactNode;
  number: string;
  visual?: React.ComponentType;
}

interface WhyNextconvertSectionProps {
  label?: string;
  title?: string;
  description?: string;
  buttonText?: string;
  buttonHref?: string;
  benefits?: Benefit[];
}

const defaultBenefits: Benefit[] = [
  {
    number: "01",
    icon: <Check className="w-5 h-5" />,
    visual: VisualOnderzoek,
    title: "Onderzoek",
    description: "Elke week duiken we diep in gebruikersgedrag met analytics en real-time data. Zo ontdekken we verborgen groeikansen, psychologische triggers en koopbezwaren.",
  },
  {
    number: "02",
    icon: <Check className="w-5 h-5" />,
    visual: VisualDesign,
    title: "Design",
    description: "Onze UX-designs combineren psychologie met esthetiek. Elk detail, van hiërarchie tot CTA, is getest om vertrouwen, emotie en actie uit te lokken. Gebaseerd op duizenden testuren en user feedback.",
  },
  {
    number: "03",
    icon: <Check className="w-5 h-5" />,
    visual: VisualOntwikkeling,
    title: "Ontwikkeling",
    description: "Geen eindeloze back-and-forths. Wij bouwen het direct in Shopify, geoptimaliseerd voor snelheid, SEO en schaalbaarheid. Je krijgt directe impact zonder afhankelijk te zijn van developers.",
  },
  {
    number: "04",
    icon: <Check className="w-5 h-5" />,
    visual: VisualAnalyseren,
    title: "Analyseren en herhalen",
    description: "We voeren doorlopend A/B-tests uit en focussen op wat écht telt: stijgende RPS, hogere LTV en lagere ROAs. Geen vage metrics. Alleen concrete groei.",
  },
];




export default function WhyNextconvertSection({
  label = "",
  title = "Onze aanpak achter converterende webshops",
  description = "Onze aanpak combineert klantdata, gebruikersgedrag en CRO-principes om te ontdekken waar omzet blijft liggen. Vanuit die inzichten ontwerpen, bouwen en optimaliseren we Shopify webshops voor hogere conversie, meer orderwaarde en winstgevende groei.",
  buttonText = "Plan gratis gesprek",
  buttonHref = "/kennismaken",
  benefits = defaultBenefits,
}: WhyNextconvertSectionProps) {
  return (
    <section className="px-4 md:px-12 py-8 md:py-24 bg-white">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-6 lg:gap-16 items-start">
          {/* Left column */}
          <div className="flex flex-col">
            {label && (
              <span className="text-accent font-semibold text-sm tracking-wide uppercase mb-2">
                {label}
              </span>
            )}
            <h2 className="text-3xl md:text-4xl lg:text-[2.5rem] font-bold text-primary leading-[1.15] mb-3">
              {title}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base leading-relaxed mb-5 md:mb-8 max-w-md">
              {description}
            </p>
            <Button
              asChild
              className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-7 h-11 rounded-full text-sm w-fit transition-all duration-300 inline-flex mb-5 lg:mb-0"
            >
              <Link to={buttonHref}>{buttonText}</Link>
            </Button>
          </div>

          {/* Right column - benefit cards */}
          <div className="flex flex-col gap-3 md:grid md:grid-cols-2 md:gap-5">
            {benefits.map((benefit, i) => (
              <div
                key={i}
                className="group relative bg-cream rounded-xl md:rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1 flex flex-col"
              >
                {benefit.visual && (
                  <div className="w-full h-32 md:h-40">
                    <benefit.visual />
                  </div>
                )}


                <div className="relative p-5 md:p-8 text-center">


                  <h3 className="text-base md:text-2xl font-bold text-primary mb-1 md:mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </div>


            ))}
          </div>

        </div>
      </div>
    </section>
  );
}
